'use client';

import { useCallback, useState } from 'react';

import { Popover } from '@base-ui/react/popover';

import { HeaderAnchor } from './header-link';
import { HEADER_OVERLAY_LAYER } from './model';
import type { HeaderNavItem } from './model';
import { useCloseAcrossBreakpoint } from './use-close-across-breakpoint';

const NAV_CONTROL_CLASS =
  'inline-flex h-11 min-h-11 cursor-pointer items-center justify-center rounded-xl border-0 bg-transparent px-4 text-[16px] font-medium tracking-[-0.8px] whitespace-nowrap text-current no-underline transition hover:bg-od-bg-hover-primary active:bg-od-bg-hover-primary focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-od-border-focused-accent-brand data-[popup-open]:bg-od-bg-hover-primary group-data-[appearance=inverse]/site-header:hover:bg-white/12 group-data-[appearance=inverse]/site-header:active:bg-white/12 group-data-[appearance=inverse]/site-header:focus-visible:outline-od-content-inverse-primary group-data-[appearance=inverse]/site-header:data-[popup-open]:bg-white/12';

function ChevronDown() {
  return (
    <svg
      aria-hidden
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      className="transition-transform group-data-[popup-open]/nav-group:rotate-180"
    >
      <path
        d="M3 5l3 3 3-3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function NavGroup({ item }: { item: Extract<HeaderNavItem, { kind: 'group' }> }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  useCloseAcrossBreakpoint({ open, close, openerVisibleOnDesktop: true });
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger className={`group/nav-group gap-1 ${NAV_CONTROL_CLASS}`}>
        {item.label}
        <ChevronDown />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner sideOffset={8} align="end" className={HEADER_OVERLAY_LAYER}>
          <Popover.Popup
            role="dialog"
            aria-label={item.label}
            className="flex min-w-60 flex-col rounded-2xl bg-od-bg-primary p-2 shadow-md outline-none"
          >
            {item.links.map((link) => (
              <HeaderAnchor
                key={link.key}
                action={link.action}
                placement="bar"
                aria-current={link.current ? 'page' : undefined}
                className="block rounded-xl px-4 py-2.5 text-base text-od-content-primary no-underline outline-none transition hover:bg-od-bg-hover-primary focus-visible:bg-od-bg-hover-primary"
                onClick={close}
              >
                {link.label}
              </HeaderAnchor>
            ))}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

export function DesktopNav({ items }: { items: HeaderNavItem[] }) {
  if (items.length === 0) return null;
  return (
    <nav aria-label="Primary" className="flex items-center">
      {items.map((item) =>
        item.kind === 'link' ? (
          <HeaderAnchor
            key={item.key}
            action={item.action}
            placement="bar"
            aria-current={item.current ? 'page' : undefined}
            className={NAV_CONTROL_CLASS}
          >
            {item.label}
          </HeaderAnchor>
        ) : (
          <NavGroup key={item.key} item={item} />
        ),
      )}
    </nav>
  );
}
